import { cn } from "@faire/prototype-playground/lib/utils";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useHistory } from "react-router-dom";

import { useCategoryMenuStyling } from "../contexts/CategoryMenuStylingContext";
import { useCompass } from "../contexts/CompassContext";
import { SurfacesMenuProvider } from "../contexts/SurfacesMenuContext";
import { isConversationalQuery } from "../utils/searchIntentDetection";
import {
  categoryTree,
  type CategoryLevel1,
  type CategoryLevel2,
} from "../view_models/categoryTree";

import SearchDropdown, { SearchDropdownHandle } from "./SearchDropdown";
import SurfacesMenu from "./SurfacesMenu";
import SurfacesMenuOverlay from "./SurfacesMenuOverlay";

// Hook to detect viewport size
function useViewport() {
  const [viewport, setViewport] = useState<"Mobile web" | "Tablet" | "Desktop">(
    () => {
      if (typeof window !== "undefined") {
        const width = window.innerWidth;
        if (width < 768) return "Mobile web";
        if (width < 1024) return "Tablet";
        return "Desktop";
      }
      return "Desktop";
    }
  );

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 768) {
        setViewport("Mobile web");
      } else if (width < 1024) {
        setViewport("Tablet");
      } else {
        setViewport("Desktop");
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return viewport;
}

// SVG Icons
function SearchIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      focusable="false"
      viewBox="0 0 24 24"
      aria-labelledby="titleAccess-search"
      role="img"
      style={{ color: "#333333", fill: "none" }}
    >
      <path
        d="M.857 10.144a9.287 9.287 0 1 0 18.573 0 9.287 9.287 0 0 0-18.573 0ZM23.143 23.143l-6.434-6.434"
        stroke="currentColor"
        strokeWidth="1"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <title id="titleAccess-search">Search</title>
    </svg>
  );
}

function NotificationIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      focusable="false"
      viewBox="0 0 24 24"
      aria-labelledby="titleAccess-notification"
      role="img"
      style={{ color: "#333333", fill: "none", fontSize: "20px" }}
    >
      <path
        d="M9.171 20.133a2.71 2.71 0 0 0 2.622 2.028 2.71 2.71 0 0 0 2.622-2.028M12.15 2.015c1.864 0 3.652.768 4.97 2.136a7.43 7.43 0 0 1 2.059 5.157c0 8.11 2.85 8.327 3.621 8.327H1.5c.786 0 3.621-.235 3.621-8.327 0-1.934.74-3.79 2.059-5.157a6.9 6.9 0 0 1 4.97-2.136Z"
        stroke="currentColor"
        strokeWidth="1"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <title id="titleAccess-notification">Notification</title>
    </svg>
  );
}

// Slate's Badge component has no dot-only variant (numeric `value` is
// required), so this is composed from Slate color tokens directly. The solid
// dot animates in (`.animate-in`, this app's fade-in-scale entrance utility)
// and stays put while a ring grows out from behind it and fades
// (Tailwind's `animate-ping`) — matching the notification-dot pattern already
// used elsewhere in this playground (workspace/rl/suma-website-link).
// Exported so other surfaces (e.g. a brand page's "more actions" menu) can
// reuse the same unread-indicator treatment instead of re-implementing it.
export function NotificationBellDot() {
  return (
    <span className="animate-in absolute top-1.5 right-1.5 flex size-2">
      <span className="bg-fs-icon-primary motion-reduce:animate-none absolute inline-flex size-full animate-ping rounded-full opacity-75" />
      <span className="bg-fs-icon-primary relative inline-flex size-2 rounded-full" />
    </span>
  );
}

function AccountIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      focusable="false"
      viewBox="0 0 24 24"
      aria-labelledby="titleAccess-account"
      role="img"
      style={{ color: "#333333", fill: "none", fontSize: "24px" }}
    >
      <path
        d="M8 6.857a4 4 0 1 0 8 0 4 4 0 0 0-8 0ZM21 20.437c-.617-1.743-1.82-3.263-3.437-4.341S14 14.437 12 14.437s-3.947.581-5.563 1.659C4.82 17.174 3.617 18.694 3 20.437z"
        stroke="currentColor"
        strokeWidth="1"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <title id="titleAccess-account">Account</title>
    </svg>
  );
}

function CartIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      focusable="false"
      viewBox="0 0 24 24"
      aria-labelledby="titleAccess-cart"
      role="img"
      style={{ color: "#333333", fill: "none", fontSize: "20px" }}
    >
      <path
        d="M20.74 14.714H7.744L6.029 5.286h16.114a.857.857 0 0 1 .843 1.01l-1.402 7.714a.86.86 0 0 1-.843.704ZM6.029 5.286l-.72-3.6A.86.86 0 0 0 4.469 1H1.743M7.743 14.714l.72 3.6a.86.86 0 0 0 .84.686h9.583"
        stroke="currentColor"
        strokeWidth="1"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M18.884 22.429a.857.857 0 1 1-1.714 0 .857.857 0 0 1 1.714 0ZM11.17 22.429a.857.857 0 1 1-1.715 0 .857.857 0 0 1 1.715 0Z"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="1"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <title id="titleAccess-cart">Cart</title>
    </svg>
  );
}

function NavCartControl({
  cartHref,
  cartCount,
  onCartClick,
  className,
  style,
}: {
  cartHref: string;
  cartCount: number;
  onCartClick?: () => void;
  className: string;
  style?: React.CSSProperties;
}) {
  const inner = (
    <div className="relative flex h-10 w-10 items-center justify-center">
      <CartIcon className="h-5 w-5" />
      {cartCount > 0 && (
        <div className="absolute -top-1 -right-1 z-10 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#333333] px-1 text-xs font-medium text-(--slate-color-action-text-inverse)">
          {cartCount > 99 ? "99+" : cartCount}
        </div>
      )}
    </div>
  );
  if (onCartClick) {
    return (
      <button
        type="button"
        data-test-id="headerCartButton"
        onClick={onCartClick}
        className={className}
        style={style}
        aria-label="Cart"
      >
        {inner}
      </button>
    );
  }
  return (
    <a
      data-test-id="headerCartButton"
      href={cartHref}
      className={className}
      style={style}
    >
      {inner}
    </a>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="12"
      height="7"
      viewBox="0 0 12 7"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ stroke: "black" }}
    >
      <path d="M1 0.994751L6 6.00005L11 0.994751" stroke="currentColor" />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M1 1l10 10M11 1L1 11"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CompassIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      focusable="false"
      width="25"
      height="25"
      viewBox="0 0 28 25"
      aria-labelledby="titleAccess-compass"
      role="img"
      style={{ color: "#333333", fontSize: "25px" }}
    >
      <path
        d="M15.5 9.5L15.9642 9.6857L16.3975 8.60247L15.3143 9.03576L15.5 9.5ZM3 14.5L2.8143 14.0358L2.93798 14.9961L3 14.5ZM9.66667 15.3333L10.1628 15.2713L10.1146 14.8854L9.72868 14.8372L9.66667 15.3333ZM10.5 22L10.0039 22.062L10.9642 22.1857L10.5 22ZM22 0.5C22 0.223858 21.7761 0 21.5 0C21.2239 0 21 0.223858 21 0.5H21.5H22ZM25.5 5C25.7761 5 26 4.77614 26 4.5C26 4.22386 25.7761 4 25.5 4V4.5V5ZM21.5 8.5H21C21 8.77172 21.217 8.9937 21.4886 8.99987C21.7603 9.00604 21.9871 8.79414 21.9995 8.5227L21.5 8.5ZM17.5 4C17.2239 4 17 4.22386 17 4.5C17 4.77614 17.2239 5 17.5 5V4.5V4ZM26 10.5C26 10.2239 25.7761 10 25.5 10C25.2239 10 25 10.2239 25 10.5H25.5H26ZM27.5 13C27.7761 13 28 12.7761 28 12.5C28 12.2239 27.7761 12 27.5 12V12.5V13ZM25.5 14.5H25C25 14.7717 25.217 14.9937 25.4886 14.9999C25.7603 15.006 25.9871 14.7941 25.9995 14.5227L25.5 14.5ZM23.5 12C23.2239 12 23 12.2239 23 12.5C23 12.7761 23.2239 13 23.5 13V12.5V12ZM20.5 14.5H20C20 19.7467 15.7467 24 10.5 24V24.5V25C16.299 25 21 20.299 21 14.5H20.5ZM10.5 24.5V24C5.25329 24 1 19.7467 1 14.5H0.5H0C0 20.299 4.70101 25 10.5 25V24.5ZM0.5 14.5H1C1 9.25329 5.25329 5 10.5 5V4.5V4C4.70101 4 0 8.70101 0 14.5H0.5ZM10.5 4.5V5C15.7467 5 20 9.25329 20 14.5H20.5H21C21 8.70101 16.299 4 10.5 4V4.5ZM15.5 9.5L15.3143 9.03576L2.8143 14.0358L3 14.5L3.1857 14.9642L15.6857 9.96424L15.5 9.5ZM3 14.5L2.93798 14.9961L9.60465 15.8295L9.66667 15.3333L9.72868 14.8372L3.06202 14.0039L3 14.5ZM9.66667 15.3333L9.17053 15.3954L10.0039 22.062L10.5 22L10.9961 21.938L10.1628 15.2713L9.66667 15.3333ZM10.5 22L10.9642 22.1857L15.9642 9.6857L15.5 9.5L15.0358 9.3143L10.0358 21.8143L10.5 22ZM21.5 0.5H21C21 1.85977 21.3081 3.00935 22.0799 3.8162C22.8557 4.62733 24.0113 5 25.5 5V4.5V4C24.1651 4 23.3208 3.66679 22.8025 3.12498C22.2802 2.57889 22 1.72846 22 0.5H21.5ZM25.5 4.5V4C24.1156 4 22.9989 4.37954 22.2161 5.17976C21.4388 5.97438 21.0626 7.1107 21.0005 8.4773L21.5 8.5L21.9995 8.5227C22.055 7.30106 22.3848 6.43739 22.9309 5.87906C23.4716 5.32634 24.2961 5 25.5 5V4.5ZM21.5 8.5H22C22 7.19357 21.6894 6.0496 20.9238 5.23182C20.1525 4.40789 18.9995 4 17.5 4V4.5V5C18.824 5 19.671 5.35682 20.1938 5.91524C20.7223 6.47981 21 7.33584 21 8.5H21.5ZM17.5 4.5V5C18.8598 5 20.0093 4.69192 20.8162 3.92014C21.6273 3.14428 22 1.98867 22 0.5H21.5H21C21 1.83486 20.6668 2.67925 20.125 3.1975C19.5789 3.71985 18.7285 4 17.5 4V4.5ZM25.5 10.5H25C25 11.2127 25.161 11.8623 25.6093 12.3309C26.0616 12.8038 26.7172 13 27.5 13V12.5V12C26.871 12 26.5266 11.8433 26.3319 11.6397C26.1331 11.4318 26 11.0814 26 10.5H25.5ZM27.5 12.5V12C26.7627 12 26.1313 12.2031 25.6793 12.6651C25.2329 13.1214 25.0332 13.7578 25.0005 14.4773L25.5 14.5L25.9995 14.5227C26.0256 13.9481 26.1789 13.5844 26.3942 13.3644C26.604 13.1499 26.9432 13 27.5 13V12.5ZM25.5 14.5H26C26 13.8112 25.8365 13.1672 25.3944 12.6951C24.9466 12.2167 24.2937 12 23.5 12V12.5V13C24.1181 13 24.4652 13.1656 24.6644 13.3785C24.8694 13.5975 25 13.9535 25 14.5H25.5ZM23.5 12.5V13C24.2127 13 24.8623 12.839 25.3309 12.3907C25.8038 11.9384 26 11.2828 26 10.5H25.5H25C25 11.129 24.8433 11.4734 24.6397 11.6681C24.4318 11.8669 24.0814 12 23.5 12V12.5Z"
        fill="#333333"
      />
      <title id="titleAccess-compass">Compass</title>
    </svg>
  );
}

function SidebarIcon({
  className,
  isOpen,
}: {
  className?: string;
  isOpen?: boolean;
}) {
  return (
    <svg
      className={className}
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ color: "#333333" }}
    >
      {/* Outer rectangle with rounded corners */}
      <rect
        x="1"
        y="4"
        width="18"
        height="12"
        rx="2"
        stroke="currentColor"
        strokeWidth="1"
        fill="none"
      />
      {/* Vertical divider line - positioned at 2/3 (66.67%) from left */}
      <line
        x1="13.33"
        y1="4"
        x2="13.33"
        y2="16"
        stroke="currentColor"
        strokeWidth="1"
      />
    </svg>
  );
}

// Category menu dropdown – Figma: Navigation to new browse surfaces (C1 > C2 > C3)
// Styling: column headers 12px #757575; links 18px #333333; active/hover = underline
function CategoryMenuPanel({
  categoryTree,
  hoveredC1Id,
  hoveredC2Id,
  onHoverC1,
  onHoverC2,
  onMouseLeavePanel,
  firstColumnFontSize,
  showColumnBorders,
  promotedColumn,
}: {
  categoryTree: CategoryLevel1[];
  hoveredC1Id: string | null;
  hoveredC2Id: string | null;
  onHoverC1: (id: string | null) => void;
  onHoverC2: (id: string | null) => void;
  onMouseLeavePanel?: () => void;
  /** When 'small', first column uses 18px like other columns; when 'large' or undefined, uses 22px. */
  firstColumnFontSize?: "large" | "small";
  /** When true, show a gray border between columns. */
  showColumnBorders?: boolean;
  /** Optional right column (e.g. sponsored rail), shown when All categories is open. */
  promotedColumn?: React.ReactNode;
}) {
  const hoveredC1 = hoveredC1Id
    ? categoryTree.find((c) => c.id === hoveredC1Id)
    : null;
  const hoveredC2 = hoveredC1?.children?.find((c) => c.id === hoveredC2Id);

  const columnHeaderClass =
    "pb-3 text-[12px] leading-4 font-normal text-[#757575]";
  const linkBaseClass =
    "inline-block py-0.5 text-[18px] leading-[26px] text-(--slate-color-grey-1000) whitespace-nowrap transition-colors duration-150 group-hover:text-[#757575] hover:text-(--slate-color-grey-1000)! focus:text-(--slate-color-grey-1000)! focus:outline-hidden hover:underline focus:underline";
  const linkCol1LargeClass =
    "inline-block py-1 text-[22px] leading-[26px] text-(--slate-color-grey-1000) whitespace-nowrap transition-colors duration-150 group-hover:text-[#757575] hover:text-(--slate-color-grey-1000)! focus:text-(--slate-color-grey-1000)! focus:outline-hidden hover:underline focus:underline";
  const linkCol1SmallClass = linkBaseClass;
  const linkCol1Class =
    firstColumnFontSize === "small" ? linkCol1SmallClass : linkCol1LargeClass;
  const linkActiveClass = "text-(--slate-color-grey-1000)! underline";

  return (
    <div
      className="group bg-fs-surface-primary flex overflow-hidden"
      onMouseLeave={onMouseLeavePanel}
    >
      {/* Column 1: C1 categories — flex-1 nav area so column height drives tallest */}
      <div className="flex min-w-[220px] flex-col py-6 pr-10 first:pl-0">
        <div className={columnHeaderClass}>Categories</div>
        <div className="min-h-0 flex-1">
          <nav
            className="flex flex-col items-start gap-0"
            aria-label="Category navigation"
          >
            {categoryTree.map((c1, index) => (
              <a
                key={c1.id}
                href={c1.href ?? "#"}
                className={cn(
                  "animate-category-link-in opacity-0",
                  linkCol1Class,
                  hoveredC1Id === c1.id ? linkActiveClass : ""
                )}
                style={{ animationDelay: `${index * 14}ms` }}
                onMouseEnter={() => onHoverC1(c1.id)}
              >
                {c1.label}
              </a>
            ))}
          </nav>
        </div>
      </div>

      {/* Column 2: C2 for hovered C1 — bordered wrapper stretches to match tallest column */}
      {hoveredC1?.children && hoveredC1.children.length > 0 && (
        <div className="flex min-w-[220px] flex-col py-6 pr-10 pl-10">
          <div className={columnHeaderClass}>{hoveredC1.label}</div>
          {showColumnBorders ? (
            <div className="-ml-10 min-h-0 flex-1 border-l border-[#dfe0e1] pl-10">
              <nav
                className="flex flex-col items-start gap-0"
                aria-label={`${hoveredC1.label} subcategories`}
              >
                {hoveredC1.children.map((c2) => (
                  <a
                    key={c2.id}
                    href={c2.href ?? "#"}
                    className={cn(
                      linkBaseClass,
                      hoveredC2Id === c2.id ? linkActiveClass : ""
                    )}
                    onMouseEnter={() => onHoverC2(c2.id)}
                  >
                    {c2.label}
                  </a>
                ))}
              </nav>
            </div>
          ) : (
            <div className="min-h-0 flex-1">
              <nav
                className="flex flex-col items-start gap-0"
                aria-label={`${hoveredC1.label} subcategories`}
              >
                {hoveredC1.children.map((c2) => (
                  <a
                    key={c2.id}
                    href={c2.href ?? "#"}
                    className={cn(
                      linkBaseClass,
                      hoveredC2Id === c2.id ? linkActiveClass : ""
                    )}
                    onMouseEnter={() => onHoverC2(c2.id)}
                  >
                    {c2.label}
                  </a>
                ))}
              </nav>
            </div>
          )}
        </div>
      )}

      {/* Column 3: C3 for hovered C2 — bordered wrapper stretches to match tallest column */}
      {hoveredC2?.children && hoveredC2.children.length > 0 && (
        <div className="flex min-w-[260px] flex-col py-6 pl-10">
          <div className={columnHeaderClass}>{hoveredC2.label}</div>
          {showColumnBorders ? (
            <div className="-ml-10 min-h-0 flex-1 border-l border-[#dfe0e1] pl-10">
              <nav
                className="flex flex-col items-start gap-0"
                aria-label={`${hoveredC2.label} subcategories`}
              >
                {hoveredC2.children.map((c3) => (
                  <a
                    key={c3.id}
                    href={c3.href ?? "#"}
                    className={linkBaseClass}
                  >
                    {c3.label}
                  </a>
                ))}
              </nav>
            </div>
          ) : (
            <div className="min-h-0 flex-1">
              <nav
                className="flex flex-col items-start gap-0"
                aria-label={`${hoveredC2.label} subcategories`}
              >
                {hoveredC2.children.map((c3) => (
                  <a
                    key={c3.id}
                    href={c3.href ?? "#"}
                    className={linkBaseClass}
                  >
                    {c3.label}
                  </a>
                ))}
              </nav>
            </div>
          )}
        </div>
      )}

      {promotedColumn ? (
        <div className="flex max-w-[min(100vw,400px)] min-w-0 shrink-0 border-l border-[#dfe0e1] pl-6">
          {promotedColumn}
        </div>
      ) : null}
    </div>
  );
}

type CommandCenterNavButtonConfig = {
  onClick: () => void;
  label?: string;
  isActive?: boolean;
};

const CommandCenterNavButton = ({
  commandCenterButton,
}: {
  commandCenterButton?: CommandCenterNavButtonConfig;
}) => {
  if (!commandCenterButton) {
    return null;
  }

  return (
    <button
      type="button"
      aria-label={commandCenterButton.label ?? "Command center"}
      aria-pressed={commandCenterButton.isActive ?? false}
      onClick={commandCenterButton.onClick}
      className={cn(
        "text-fs-text-primary shrink-0 rounded-full border px-4 py-2 text-sm transition-colors duration-300",
        commandCenterButton.isActive
          ? "border-fs-action-border-default bg-[#f7f7f7]"
          : "border-[#dfe0e1] hover:border-[#333333]"
      )}
    >
      {commandCenterButton.label ?? "Command center"}
    </button>
  );
};

// Logged-out identity cluster — replaces the bell/account/cart group when
// `signedIn` is false, so signed-out states show a visitor-style nav.
//
// The hide flags are opt-in and default to showing everything, so the full
// marketplace visitor nav is unchanged for every page that doesn't ask. A
// brand-landing surface (e.g. a Faire Direct page reached from the brand's own
// site) opts out of the ones that are off-task there — a visitor who came to
// buy from one brand is not choosing a locale or signing up to sell.
function LoggedOutActions({
  hideLanguageSelector = false,
  hideSellerSignup = false,
  hideBlogLink = false,
}: {
  hideLanguageSelector?: boolean;
  hideSellerSignup?: boolean;
  hideBlogLink?: boolean;
}) {
  const textLink =
    "text-fs-text-primary flex h-10 cursor-pointer items-center rounded-[8px] border-0 bg-transparent px-3 text-sm transition-colors duration-300 ease-in-out hover:bg-[#f7f7f7]";
  return (
    <div className="flex items-center gap-1">
      {hideLanguageSelector ? null : (
        <button
          type="button"
          aria-label="Language selector"
          className="hidden h-10 cursor-pointer items-center gap-2 rounded-full border-0 bg-transparent px-3 hover:bg-[#fbfbfb] lg:flex"
        >
          <svg
            className="h-4 w-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
          </svg>
          <span className="text-fs-text-primary text-sm">EN-US</span>
        </button>
      )}
      {hideBlogLink ? null : (
        <button type="button" className={textLink}>
          Blog
        </button>
      )}
      {hideSellerSignup ? null : (
        <button type="button" className={textLink}>
          Sign up to sell
        </button>
      )}
      <button type="button" className={textLink}>
        Sign in
      </button>
      <button
        type="button"
        className="text-fs-text-primary-inverse flex h-10 cursor-pointer items-center rounded-[8px] border-0 bg-[#333333] px-4 text-sm transition-colors duration-300 ease-in-out hover:bg-(--slate-color-grey-1000)"
      >
        Sign up to buy
      </button>
    </div>
  );
}

export interface RetailerBottomNavLink {
  label: string;
  href: string;
  id?: string;
  isSale?: boolean;
  popover?: React.ReactNode;
  ariaControls?: string;
  ariaExpanded?: boolean;
  ariaHasPopup?: React.AriaAttributes["aria-haspopup"];
  onBlur?: React.FocusEventHandler<HTMLAnchorElement>;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  onFocus?: React.FocusEventHandler<HTMLAnchorElement>;
  onMouseEnter?: React.MouseEventHandler<HTMLAnchorElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLAnchorElement>;
}

type RenderedBottomNavLink = RetailerBottomNavLink & { testId: string };

type RetailerGlobalNavLoggedInProps = {
  languageSelector?: boolean;
  device?: "Desktop" | "Tablet" | "Mobile web";
  cartCount?: number;
  focused?: boolean;
  focused2?: boolean;
  hideSearch?: boolean; // Hide the nav search bar
  hideAllCategories?: boolean; // Hide the All categories button and mega-menu
  hideNotifications?: boolean; // Hide the notifications bell in the global nav row
  hideAccountIcon?: boolean; // Hide the account/profile icon in the global nav row
  hideCartIcon?: boolean; // Hide the cart icon in the global nav row
  /** Optional icon button rendered directly to the right of the cart control (e.g. a sidecart toggle). */
  sidecartNavButton?: {
    onClick: () => void;
    isOpen: boolean;
    label?: string;
  };
  /** When false, the identity cluster (bell/account/cart) is replaced with Sign in / Sign up (logged-out nav). Default true. */
  signedIn?: boolean;
  hideLanguageSelector?: boolean; // Logged-out nav: hide the EN-US locale button
  hideSellerSignup?: boolean; // Logged-out nav: hide the "Sign up to sell" link
  hideBlogLink?: boolean; // Logged-out nav: hide the "Blog" link
  /** Fixed height (px) for the logo/actions row, vertically centering its
   * contents. Omit to keep the default auto-height row with its top pad. */
  logoRowHeight?: number;
  showBottomNavLinks?: boolean; // When set, overrides bottom nav visibility (default: !hideSearch)
  navLinksInLogoRow?: boolean; // Render bottom nav links on the logo row instead of a second row
  bottomNavLinksOverride?: RetailerBottomNavLink[]; // Replace default bottom nav links
  navSearchBar?: React.ReactNode; // Search bar to show in nav
  sticky?: boolean; // Whether the nav should be sticky (default: true)
  contentMaxWidth?: string; // Max width for content area (affects nav width too)
  setupGuideButton?: {
    onClick: () => void;
    label?: string;
  }; // Optional setup guide button
  commandCenterButton?: CommandCenterNavButtonConfig; // Optional command center toggle
  compassPanelBehavior?: boolean; // Show compass sidebar icon button
  onCompassClick?: () => void; // Handler for compass button click
  logoLinkUrl?: string; // Custom URL for the logo link (default: "/homepage")
  isCompassPanelOpen?: boolean; // Whether the compass panel is currently open
  compassPanelVariant?: number; // Variant: 1 = nav icon, 2 = nav icon (stays in nav)
  disableLogoLink?: boolean; // Disable logo link navigation
  defaultSearchValue?: string; // Initial value for search bar
  searchPlaceholder?: string; // Placeholder for the search input
  onSearchSubmit?: (query: string) => void; // Called when user submits a search
  controlledSearchValue?: string; // When set, nav search bar value is controlled externally
  /** When set, logo stays in the current prototype instead of navigating to logoLinkUrl. */
  onLogoClick?: () => void;
  extraNavLinks?: { label: string; href: string }[]; // Extra links prepended before "Brand updates"
  cartHref?: string; // Cart link URL (default: "/cart")
  /** When set, cart control opens this handler instead of navigating to cartHref */
  onCartClick?: () => void;
  /** Hides the nav cart icon entirely (not just its badge) — for surfaces where a different, dedicated cart entry point replaces it. Default false so every existing prototype is unaffected. */
  hideCart?: boolean;
  onFaireAiClick?: () => void; // Handler for Faire AI button click
  isFaireAiOpen?: boolean; // Whether the Faire AI panel is currently open
  onAccountClick?: () => void; // Handler for account button click (overrides default behavior)
  onNotificationClick?: () => void; // Handler for notification bell click (overrides default Compass-shortcut behavior)
  hasNotificationDot?: boolean; // Show an animated unread-notification dot on the bell icon
  accountOverdueDot?: boolean; // Show red overdue dot on account icon
  /**
   * Renders an account dropdown anchored under the account glyph. When set,
   * clicking the glyph toggles the dropdown (instead of `onAccountClick`);
   * clicking away or pressing Escape closes it. The renderer receives a
   * `close` callback so menu items can dismiss the dropdown themselves.
   */
  accountDropdown?: (close: () => void) => React.ReactNode;
  /** Hides the account/profile icon entirely — for surfaces where a different, dedicated account entry point replaces it (e.g. a persistent right-rail nav). Default false so every existing prototype is unaffected. */
  hideAccount?: boolean;
  /** Optional right column in the All categories mega-menu (passed through from RetailerLayout). */
  categoryMenuPromotedColumn?: React.ReactNode;
};

function RetailerGlobalNavLoggedInInner({
  languageSelector = false,
  hideLanguageSelector = false,
  hideSellerSignup = false,
  hideBlogLink = false,
  logoRowHeight,
  device,
  cartCount = 0,
  focused = false,
  focused2 = false,
  hideSearch = false,
  hideAllCategories = false,
  hideNotifications = false,
  hideAccountIcon = false,
  hideCartIcon = false,
  sidecartNavButton,
  signedIn = true,
  showBottomNavLinks,
  navLinksInLogoRow = false,
  bottomNavLinksOverride,
  navSearchBar = null,
  sticky = true,
  contentMaxWidth = "1920px",
  setupGuideButton,
  commandCenterButton,
  compassPanelBehavior = false,
  onCompassClick,
  isCompassPanelOpen = false,
  compassPanelVariant = 1,
  disableLogoLink = false,
  logoLinkUrl = "/homepage",
  defaultSearchValue = "",
  searchPlaceholder = "What are you looking for?",
  onSearchSubmit,
  controlledSearchValue,
  onLogoClick,
  extraNavLinks = [],
  cartHref = "/cart",
  onCartClick,
  hideCart = false,
  onFaireAiClick,
  isFaireAiOpen = false,
  onAccountClick,
  onNotificationClick,
  hasNotificationDot = false,
  accountOverdueDot = false,
  accountDropdown,
  hideAccount = false,
  categoryMenuPromotedColumn,
}: RetailerGlobalNavLoggedInProps) {
  const detectedDevice = useViewport();
  const currentDevice = device || detectedDevice;
  const location = useLocation();
  const history = useHistory();
  const [searchValue, setSearchValue] = useState(
    controlledSearchValue ?? defaultSearchValue
  );

  // Keep internal state in sync when controlled value changes externally
  React.useEffect(() => {
    if (controlledSearchValue !== undefined) {
      setSearchValue(controlledSearchValue);
    }
  }, [controlledSearchValue]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const { togglePanel, openPanel, clearMessages, closePanel, state } =
    useCompass();
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const searchDropdownRef = useRef<SearchDropdownHandle>(null);
  const [categoriesMenuOpen, setCategoriesMenuOpen] = useState(false);
  const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState(false);
  const closeAccountDropdown = useCallback(
    () => setIsAccountDropdownOpen(false),
    []
  );
  const toggleAccountDropdown = useCallback(
    () => setIsAccountDropdownOpen((open) => !open),
    []
  );

  // Close the account dropdown when clicking away from it or pressing Escape.
  useEffect(() => {
    if (!isAccountDropdownOpen) {
      return;
    }
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target;
      if (
        target instanceof Element &&
        !target.closest("[data-account-dropdown-root]")
      ) {
        setIsAccountDropdownOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsAccountDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isAccountDropdownOpen]);
  const [hoveredC1Id, setHoveredC1Id] = useState<string | null>(null);
  const [hoveredC2Id, setHoveredC2Id] = useState<string | null>(null);
  const categoriesMenuRef = useRef<HTMLDivElement>(null);
  const navRowRef = useRef<HTMLDivElement>(null);
  const [navRowHeight, setNavRowHeight] = useState(0);
  const categoryMenuStyling = useCategoryMenuStyling();

  // Dev shortcut: Click bell to open Compass with preset query
  const handleNotificationClick = () => {
    if (isCompassEnabled) {
      openPanel("search", "New York City hotel room gifts for guests");
    }
  };

  // Reset prototype to beginning when clicking person icon
  const handleProfileClick = useCallback(() => {
    // Close panel first
    closePanel();
    // Clear all Compass state first
    clearMessages();
    // Navigate to home page (use replace to avoid back button issues)
    history.replace("/template");
  }, [closePanel, clearMessages, history]);

  // Enable Compass on template, category, and PDP routes (Compass prototype)
  const isCompassEnabled =
    location.pathname === "/template" ||
    location.pathname.startsWith("/category/") ||
    location.pathname === "/pdp";

  // Keyboard shortcut: CMD/CTRL + Shift + R to restart prototype
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        (e.metaKey || e.ctrlKey) &&
        e.shiftKey &&
        e.key.toLowerCase() === "r"
      ) {
        e.preventDefault();
        handleProfileClick();
      }
    };

    if (isCompassEnabled) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [isCompassEnabled, handleProfileClick]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        setShowSearchDropdown(false);
      }
    };

    if (showSearchDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSearchDropdown]);

  useEffect(() => {
    setSearchValue(defaultSearchValue);
  }, [defaultSearchValue]);

  // Close categories menu when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        categoriesMenuRef.current &&
        !categoriesMenuRef.current.contains(event.target as Node)
      ) {
        setCategoriesMenuOpen(false);
        setHoveredC1Id(null);
        setHoveredC2Id(null);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setCategoriesMenuOpen(false);
        setHoveredC1Id(null);
        setHoveredC2Id(null);
      }
    };
    if (categoriesMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscape);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [categoriesMenuOpen]);

  // Measure nav row height when menu opens so the overlay starts below it
  useEffect(() => {
    if (categoriesMenuOpen && navRowRef.current) {
      setNavRowHeight(navRowRef.current.offsetHeight);
    }
  }, [categoriesMenuOpen]);

  const handleSearchFocus = () => {
    setShowSearchDropdown(true);
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setShowSearchDropdown(true);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      // If an external submit handler is provided, use it instead of navigating
      if (onSearchSubmit) {
        onSearchSubmit(searchValue.trim());
        return;
      }
      // If it's a conversational query and Compass is enabled, use Compass dropdown
      if (
        isCompassEnabled &&
        isConversationalQuery(searchValue) &&
        searchDropdownRef.current
      ) {
        searchDropdownRef.current.selectHighlighted();
      } else if (searchValue.trim()) {
        // Retailer nav search: split-grid results prototype (SearchResultsPage still at /search-results)
        history.push(
          `/sc/search-split-grid-results?q=${encodeURIComponent(searchValue.trim())}`
        );
      }
    }
  };

  const handleLogoClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (onLogoClick === undefined) {
      return;
    }
    event.preventDefault();
    onLogoClick();
  };

  // Production bottom nav links
  const defaultBottomNavLinks: RenderedBottomNavLink[] = [
    ...extraNavLinks.map((l) => ({
      label: l.label,
      href: l.href,
      testId: `header-extra-${l.label.toLowerCase().replace(/\s+/g, "_")}`,
      isSale: false,
    })),
    // {
    //   label: "Ideas Hub",
    //   href: "/ideas-hub",
    //   testId: "header-ideas_hub-dropdown-link",
    // },
    {
      label: "Brand updates",
      href: "/my-brands/updates",
      testId: "header-my_brands-dropdown-link",
    },
    {
      label: "Buy again",
      href: "/category/Buy Again",
      testId: "header-buy_again-dropdown-link",
    },
    {
      label: "Bestsellers",
      href: "/category/Bestsellers",
      testId: "header-bestsellers-dropdown-link",
    },
    {
      label: "Trending",
      href: "/category/Trending",
      testId: "header-trending-dropdown-link",
    },
    {
      label: "Local",
      href: "/category/Local",
      testId: "header-local-dropdown-link",
    },
    {
      label: "New products",
      href: "/category/New Products",
      testId: "header-new_products-dropdown-link",
    },
    {
      label: "New brands",
      href: "/category/New Brands",
      testId: "header-new_brands-dropdown-link",
    },
    {
      label: "Gifts",
      href: "/category/Gifts",
      testId: "header-gifts-dropdown-link",
    },
    {
      label: "Home decor",
      href: "/category/Home Decor",
      testId: "header-home_living-dropdown-link",
    },
    {
      label: "Food & drink",
      href: "/category/Food & Drink",
      testId: "header-food_drink-dropdown-link",
    },
    {
      label: "Women",
      href: "/category/Women",
      testId: "header-women-dropdown-link",
    },
    {
      label: "Beauty & wellness",
      href: "/category/Beauty & Wellness",
      testId: "header-beauty_wellness-dropdown-link",
    },
    {
      label: "Sale",
      href: "/category/Sale",
      testId: "header-sale-dropdown-link",
      isSale: true,
    },
  ];

  const bottomNavLinks: RenderedBottomNavLink[] = (() => {
    if (bottomNavLinksOverride == null) {
      return defaultBottomNavLinks;
    }

    const seenLabels = new Set<string>();
    return bottomNavLinksOverride
      .filter((link) => {
        const labelKey = link.label.trim().toLowerCase();
        if (seenLabels.has(labelKey)) {
          return false;
        }
        seenLabels.add(labelKey);
        return true;
      })
      .map((link) => ({
        ...link,
        testId: `header-activity-${link.label.toLowerCase().replace(/\s+/g, "_")}`,
        isSale: link.isSale ?? false,
      }));
  })();

  const shouldShowLogoRowNavLinks =
    navLinksInLogoRow && bottomNavLinks.length > 0;
  const shouldShowBottomNavLinks =
    (showBottomNavLinks ?? !hideSearch) && !navLinksInLogoRow;
  const shouldShowSearchBar = !hideSearch;
  const shouldShowAllCategories = !hideAllCategories;
  const hasBottomNavPopovers = bottomNavLinks.some(
    (link) => link.popover != null
  );

  const renderNavLinks = (
    links: typeof bottomNavLinks,
    wrapperClassName?: string
  ) => (
    <div className={wrapperClassName}>
      {links.map((link) => (
        <div
          key={`${link.label}-${link.href}`}
          className="relative flex shrink-0 cursor-pointer px-0.5 py-1"
        >
          <a
            aria-controls={link.ariaControls}
            aria-expanded={link.ariaExpanded}
            aria-haspopup={link.ariaHasPopup}
            className={cn(
              "rounded-[8px] px-2 py-1.5 text-sm leading-5 whitespace-nowrap transition-colors duration-500 ease-in-out hover:bg-[#f7f7f7] focus:text-[#757575] active:text-[#757575]",
              link.isSale ? "text-[#7d3e1e]" : "text-fs-text-primary"
            )}
            aria-label={link.label}
            data-test-id={link.testId}
            href={link.href}
            id={link.id}
            onBlur={link.onBlur}
            onClick={link.onClick}
            onFocus={link.onFocus}
            onMouseEnter={link.onMouseEnter}
            onMouseLeave={link.onMouseLeave}
          >
            {link.label}
          </a>
          {link.popover}
        </div>
      ))}
    </div>
  );

  useEffect(() => {
    if (hideAllCategories && categoriesMenuOpen) {
      setCategoriesMenuOpen(false);
      setHoveredC1Id(null);
      setHoveredC2Id(null);
    }
  }, [hideAllCategories, categoriesMenuOpen]);

  // Tablet layout (768–1024): hamburger to the left of logo, no single-line element
  if (currentDevice === "Tablet") {
    return (
      <header
        className={cn(
          "bg-fs-surface-primary relative z-600 flex w-full flex-col items-stretch border-b border-[#dfe0e1] print:hidden",
          isAccountDropdownOpen ? "overflow-visible" : "overflow-x-auto"
        )}
      >
        <div
          className="m-auto flex w-full items-center justify-center gap-4 px-4 py-2 transition-all duration-300 ease-out lg:px-12"
          style={{
            maxWidth: contentMaxWidth,
            width: "100%",
            minWidth: "fit-content",
          }}
        >
          <SurfacesMenu />
          {disableLogoLink ? (
            <div
              id="faire-logo-link"
              aria-label="Faire Logo"
              className="relative h-6 w-[98px]"
            >
              <img
                alt="Faire Logo"
                src="https://cdn.faire.com/static/logo.svg"
                className="h-full w-full object-contain"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src =
                    "http://localhost:3845/assets/1e3ffc68be20eda669774f7388f9632f2f0bab67.svg";
                }}
              />
            </div>
          ) : (
            <a
              id="faire-logo-link"
              aria-label="Go to Faire homepage"
              href={logoLinkUrl}
              onClick={handleLogoClick}
              className="relative h-6 w-[98px]"
            >
              <img
                alt="Faire Logo"
                src="https://cdn.faire.com/static/logo.svg"
                className="h-full w-full object-contain"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src =
                    "http://localhost:3845/assets/1e3ffc68be20eda669774f7388f9632f2f0bab67.svg";
                }}
              />
            </a>
          )}
          {shouldShowLogoRowNavLinks
            ? renderNavLinks(
                bottomNavLinks,
                "flex min-w-0 shrink items-center overflow-x-auto"
              )
            : null}
          <div
            className="flex-1"
            style={{ position: "relative" }}
            ref={searchContainerRef}
          >
            {shouldShowSearchBar ? (
              <>
                <div className="bg-fs-surface-primary flex h-10 items-center gap-2 rounded-full border border-[var(--slate-color-action-border-subdued)] px-4 pr-5">
                  <input
                    id="top-search"
                    placeholder={searchPlaceholder}
                    aria-label={searchPlaceholder}
                    autoComplete="off"
                    data-test-id="searchBarInput"
                    className="text-fs-text-primary flex-1 border-0 bg-transparent text-sm outline-0 placeholder:text-[#757575]"
                    value={searchValue}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    onFocus={handleSearchFocus}
                    onKeyDown={handleSearchKeyDown}
                  />
                  <div className="flex items-center justify-center">
                    <SearchIcon className="h-4 w-4" />
                  </div>
                </div>
                {showSearchDropdown && isCompassEnabled && (
                  <SearchDropdown
                    ref={searchDropdownRef}
                    searchQuery={searchValue}
                    onClose={() => setShowSearchDropdown(false)}
                  />
                )}
              </>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            {isCompassEnabled && (
              <button
                data-test-id="compassButton"
                onClick={togglePanel}
                className={cn(
                  "flex h-10 w-10 cursor-pointer items-center justify-center rounded-[8px] border-0 bg-transparent p-0 transition-all duration-300 ease-in-out hover:bg-[#f7f7f7]",
                  state.isPanelOpen
                    ? "pointer-events-none scale-95 opacity-0"
                    : "pointer-events-auto scale-100 opacity-100"
                )}
                aria-label="Compass"
                type="button"
              >
                <div className="relative flex h-10 w-10 items-center justify-center">
                  <CompassIcon className="h-[25px] w-[25px]" />
                </div>
              </button>
            )}
            {setupGuideButton && (
              <button
                aria-label={setupGuideButton.label || "Setup guide"}
                type="button"
                onClick={setupGuideButton.onClick}
                className="text-fs-text-primary mr-2 rounded-full border border-[#dfe0e1] px-4 py-2 text-sm transition-colors duration-300 hover:border-[#333333]"
              >
                {setupGuideButton.label || "Setup guide"}
              </button>
            )}
            <CommandCenterNavButton commandCenterButton={commandCenterButton} />
            {hideNotifications ? null : (
              <button
                data-test-id="notificationDropdown"
                className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-[8px] border-0 bg-transparent p-0 transition-colors duration-500 ease-in-out hover:bg-[#f7f7f7]"
                aria-label="Notifications"
                type="button"
                onClick={onNotificationClick || handleNotificationClick}
              >
                <div className="relative flex h-10 w-10 items-center justify-center">
                  <NotificationIcon className="h-5 w-5" />
                  {hasNotificationDot ? <NotificationBellDot /> : null}
                </div>
              </button>
            )}
            {hideAccountIcon || hideAccount ? null : (
              <div className="relative" data-account-dropdown-root>
                <button
                  aria-label="My Profile"
                  data-test-id="accountDropdown"
                  aria-expanded={
                    accountDropdown ? isAccountDropdownOpen : undefined
                  }
                  aria-haspopup={accountDropdown ? "menu" : undefined}
                  className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-[8px] border-0 bg-transparent p-0 transition-colors duration-500 ease-in-out hover:bg-[#f7f7f7]"
                  type="button"
                  onClick={
                    accountDropdown
                      ? toggleAccountDropdown
                      : onAccountClick || handleProfileClick
                  }
                >
                  <div className="flex h-10 w-10 items-center justify-center">
                    <div className="relative">
                      <AccountIcon className="h-6 w-6" />
                      {accountOverdueDot && (
                        <div className="absolute -top-[3px] -right-[3px] size-[8px] rounded-full bg-[#d14a3e]" />
                      )}
                    </div>
                  </div>
                </button>
                {accountDropdown && isAccountDropdownOpen ? (
                  <div className="absolute top-full -right-12 z-[700] mt-2">
                    {accountDropdown(closeAccountDropdown)}
                  </div>
                ) : null}
              </div>
            )}
            {onFaireAiClick && (
              <button
                aria-label="Faire AI"
                className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-[8px] border-0 bg-transparent p-0 transition-colors duration-500 ease-in-out hover:bg-(--slate-color-action-surface-inverse-hover)"
                type="button"
                onClick={onFaireAiClick}
                style={{
                  color: isFaireAiOpen ? "#333" : undefined,
                }}
              >
                <div className="relative flex h-10 w-10 items-center justify-center">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6.99984 3.33301C6.99984 7.13048 8.92389 9.25706 13.3332 9.33301C9.24456 9.33301 7.24034 11.3836 6.99984 15.333C6.99984 11.6115 5.23612 9.33301 0.666504 9.33301C4.75511 9.25706 6.99984 7.43427 6.99984 3.33301Z"
                      stroke="#333333"
                      strokeWidth="0.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M12.6667 0.666992C12.6667 2.39248 13.451 3.33366 15.3333 3.33366C13.6078 3.33366 12.7451 4.27484 12.6667 6.00033C12.6667 4.35327 11.8824 3.33366 10 3.33366C11.7255 3.33366 12.6667 2.54935 12.6667 0.666992Z"
                      stroke="#333333"
                      strokeWidth="0.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </button>
            )}
            {hideCartIcon || hideCart ? null : (
              <NavCartControl
                cartHref={cartHref}
                cartCount={cartCount}
                onCartClick={onCartClick}
                className="relative flex items-center justify-center rounded-[8px] transition-colors duration-500 ease-in-out hover:bg-(--slate-color-action-surface-inverse-hover)"
                style={{ height: "unset", width: "40px" }}
              />
            )}
            {sidecartNavButton && (
              <button
                data-test-id="sidecartNavButton"
                aria-label={sidecartNavButton.label ?? "Cart sidebar"}
                className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-[8px] border-0 bg-transparent p-0 transition-colors duration-500 ease-in-out hover:bg-[#f7f7f7]"
                type="button"
                onClick={sidecartNavButton.onClick}
              >
                <div className="relative flex h-10 w-10 items-center justify-center">
                  <SidebarIcon
                    className="h-5 w-5"
                    isOpen={sidecartNavButton.isOpen}
                  />
                </div>
              </button>
            )}
            {compassPanelBehavior && !isCompassPanelOpen && (
              <button
                data-test-id="compassSidebarButton"
                className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-[8px] border-0 bg-transparent p-0 transition-colors duration-500 ease-in-out hover:bg-[#f7f7f7]"
                aria-label="Compass Sidebar"
                type="button"
                onClick={onCompassClick}
              >
                <div className="relative flex h-10 w-10 items-center justify-center">
                  <SidebarIcon
                    className="h-5 w-5"
                    isOpen={isCompassPanelOpen}
                  />
                </div>
              </button>
            )}
          </div>
        </div>
      </header>
    );
  }

  // Mobile web layout
  if (currentDevice === "Mobile web") {
    return (
      <header className="bg-fs-surface-primary relative z-600 flex h-[112px] w-full flex-col items-stretch border-b border-[#dfe0e1] print:hidden">
        <div className="relative h-[112px] w-full" ref={searchContainerRef}>
          <div className="bg-fs-surface-primary absolute top-0 left-0 h-[112px] w-full" />
          <div className="bg-fs-surface-primary absolute top-14 right-4 left-4 flex h-10 items-center gap-2 rounded-full border border-[var(--slate-color-action-border-subdued)] px-4 pr-5">
            <input
              id="top-search"
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              autoComplete="off"
              data-test-id="searchBarInput"
              className="text-fs-text-primary flex-1 border-0 bg-transparent text-sm outline-0 placeholder:text-[#757575]"
              value={searchValue}
              onChange={(e) => handleSearchChange(e.target.value)}
              onFocus={handleSearchFocus}
              onKeyDown={handleSearchKeyDown}
            />
            <div className="flex items-center justify-center">
              <SearchIcon className="h-4 w-4" />
            </div>
          </div>
          {showSearchDropdown && isCompassEnabled && (
            <div className="absolute top-[72px] right-4 left-4">
              <SearchDropdown
                ref={searchDropdownRef}
                searchQuery={searchValue}
                onClose={() => setShowSearchDropdown(false)}
              />
            </div>
          )}
          <div className="absolute top-2 left-2 z-10">
            <SurfacesMenu />
          </div>
          {disableLogoLink ? (
            <div
              id="faire-logo-link"
              aria-label="Faire Logo"
              className="absolute top-4 left-[60px] h-6 w-[98px]"
            >
              <img
                alt="Faire Logo"
                src="https://cdn.faire.com/static/logo.svg"
                className="h-full w-full object-contain"
              />
            </div>
          ) : (
            <a
              id="faire-logo-link"
              aria-label="Go to Faire homepage"
              href={logoLinkUrl}
              onClick={handleLogoClick}
              className="absolute top-4 left-[60px] h-6 w-[98px]"
            >
              <img
                alt="Faire Logo"
                src="https://cdn.faire.com/static/logo.svg"
                className="h-full w-full object-contain"
              />
            </a>
          )}
          {hideCart ? null : (
            <NavCartControl
              cartHref={cartHref}
              cartCount={cartCount}
              onCartClick={onCartClick}
              className="absolute flex h-10 w-10 items-center justify-center rounded-[8px] transition-colors duration-500 ease-in-out hover:bg-(--slate-color-action-surface-inverse-hover)"
              style={
                compassPanelBehavior
                  ? { right: "48px", top: "8px" }
                  : { right: "8px", top: "8px" }
              }
            />
          )}
          {compassPanelBehavior && !isCompassPanelOpen && (
            <button
              data-test-id="compassSidebarButton"
              className="absolute flex h-10 w-10 cursor-pointer items-center justify-center rounded-[8px] border-0 bg-transparent p-0 transition-colors duration-500 ease-in-out hover:bg-[#f7f7f7]"
              style={{
                right: compassPanelBehavior ? "48px" : "8px",
                top: "8px",
              }}
              aria-label="Compass Sidebar"
              type="button"
              onClick={onCompassClick}
            >
              <div className="relative flex h-10 w-10 items-center justify-center">
                <SidebarIcon className="h-5 w-5" isOpen={isCompassPanelOpen} />
              </div>
            </button>
          )}
          {isCompassEnabled && (
            <button
              data-test-id="compassButton"
              onClick={togglePanel}
              className={cn(
                "absolute top-2 right-[88px] flex h-10 w-10 cursor-pointer items-center justify-center rounded-[8px] border-0 bg-transparent p-0 transition-all duration-300 ease-in-out hover:bg-[#f7f7f7]",
                state.isPanelOpen
                  ? "pointer-events-none scale-95 opacity-0"
                  : "pointer-events-auto scale-100 opacity-100"
              )}
              aria-label="Compass"
              type="button"
            >
              <div className="relative flex h-10 w-10 items-center justify-center">
                <CompassIcon className="h-5 w-5" />
              </div>
            </button>
          )}
          {commandCenterButton ? (
            <div
              className="absolute top-2"
              style={{
                right: isCompassEnabled
                  ? "136px"
                  : compassPanelBehavior
                    ? "184px"
                    : "100px",
              }}
            >
              <CommandCenterNavButton
                commandCenterButton={commandCenterButton}
              />
            </div>
          ) : null}
          {hideNotifications ? null : (
            <button
              data-test-id="notificationDropdown"
              className="absolute top-2 flex h-10 w-10 cursor-pointer items-center justify-center rounded-[8px] border-0 bg-transparent p-0 transition-colors duration-500 ease-in-out hover:bg-[#f7f7f7]"
              style={
                commandCenterButton
                  ? isCompassEnabled
                    ? { right: "188px" }
                    : compassPanelBehavior
                      ? { right: "236px" }
                      : { right: "192px" }
                  : isCompassEnabled
                    ? { right: "48px" }
                    : compassPanelBehavior
                      ? { right: "96px" }
                      : { right: "52px" }
              }
              aria-label="Notifications"
              type="button"
              onClick={onNotificationClick || handleNotificationClick}
            >
              <div className="relative flex h-10 w-10 items-center justify-center">
                <NotificationIcon className="h-5 w-5" />
                {hasNotificationDot ? <NotificationBellDot /> : null}
              </div>
            </button>
          )}
        </div>
      </header>
    );
  }

  // Focused variant - only logo (centered, same position as full variant)
  if (focused) {
    return (
      <header
        className={cn(
          sticky ? "sticky top-0" : "",
          "bg-fs-surface-primary z-600 flex w-full flex-col items-stretch border-b border-[#dfe0e1] print:hidden"
        )}
      >
        <div
          className="m-auto flex w-full items-center justify-center px-4 py-2 lg:px-12"
          style={{ maxWidth: contentMaxWidth, width: "100%" }}
        >
          <button
            aria-label="Menu"
            className="bg-fs-surface-primary flex h-10 w-10 items-center justify-center rounded-full p-2.5 lg:hidden"
            type="button"
          >
            <div className="h-0.5 w-5 bg-[#333333]"></div>
          </button>
          {disableLogoLink ? (
            <div
              id="faire-logo-link"
              aria-label="Faire Logo"
              className="relative h-6 w-[98px] shrink-0"
            >
              <img
                alt="Faire Logo"
                src="https://cdn.faire.com/static/logo.svg"
                className="h-full w-full object-contain"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src =
                    "http://localhost:3845/assets/1e3ffc68be20eda669774f7388f9632f2f0bab67.svg";
                }}
              />
            </div>
          ) : (
            <a
              id="faire-logo-link"
              aria-label="Go to Faire homepage"
              href={logoLinkUrl}
              onClick={handleLogoClick}
              className="relative h-6 w-[98px] shrink-0"
            >
              <img
                alt="Faire Logo"
                src="https://cdn.faire.com/static/logo.svg"
                className="h-full w-full object-contain"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src =
                    "http://localhost:3845/assets/1e3ffc68be20eda669774f7388f9632f2f0bab67.svg";
                }}
              />
            </a>
          )}
        </div>
      </header>
    );
  }

  // Focused variant 2 - only logo (left-aligned, same position as standard variant)
  // Adjusted padding to account for search bar height in standard variant
  if (focused2) {
    return (
      <header
        className={cn(
          sticky ? "sticky top-0" : "",
          "bg-fs-surface-primary z-600 flex w-full flex-col items-stretch border-b border-[#dfe0e1] print:hidden"
        )}
      >
        <div
          className="m-auto flex w-full items-center px-4 pt-5 pb-5 lg:px-12 lg:pt-5"
          style={{ maxWidth: contentMaxWidth, width: "100%" }}
        >
          <button
            aria-label="Menu"
            className="bg-fs-surface-primary flex h-10 w-10 items-center justify-center rounded-full p-2.5 lg:hidden"
            type="button"
          >
            <div className="h-0.5 w-5 bg-[#333333]"></div>
          </button>
          {disableLogoLink ? (
            <div
              id="faire-logo-link"
              aria-label="Faire Logo"
              className="relative h-6 w-[98px] shrink-0"
            >
              <img
                alt="Faire Logo"
                src="https://cdn.faire.com/static/logo.svg"
                className="h-full w-full object-contain"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src =
                    "http://localhost:3845/assets/1e3ffc68be20eda669774f7388f9632f2f0bab67.svg";
                }}
              />
            </div>
          ) : (
            <a
              id="faire-logo-link"
              aria-label="Go to Faire homepage"
              href={logoLinkUrl}
              onClick={handleLogoClick}
              className="relative h-6 w-[98px] shrink-0"
            >
              <img
                alt="Faire Logo"
                src="https://cdn.faire.com/static/logo.svg"
                className="h-full w-full object-contain"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src =
                    "http://localhost:3845/assets/1e3ffc68be20eda669774f7388f9632f2f0bab67.svg";
                }}
              />
            </a>
          )}
        </div>
      </header>
    );
  }

  // Desktop layout
  return (
    <header
      className={cn(
        sticky ? "sticky top-0" : "",
        "bg-fs-surface-primary z-600 flex w-full flex-col items-stretch border-b border-[#dfe0e1] print:hidden",
        categoriesMenuOpen ||
          navSearchBar ||
          hasBottomNavPopovers ||
          isAccountDropdownOpen
          ? "overflow-visible"
          : "overflow-x-auto"
      )}
    >
      <div
        ref={categoriesMenuRef}
        className={cn("relative w-full", categoriesMenuOpen ? "z-700" : "")}
      >
        {/* Top Nav - ref for measuring height so overlay starts below it */}
        <div
          ref={navRowRef}
          className={cn(
            "flex w-full items-center justify-center px-4 transition-all duration-300 ease-out lg:px-12",
            // Default keeps the historical asymmetric top pad. An explicit
            // `logoRowHeight` opts into a fixed-height row whose contents are
            // centered on both axes instead.
            logoRowHeight === undefined && "pt-2"
          )}
          style={{
            ...(logoRowHeight === undefined ? {} : { height: logoRowHeight }),
            maxWidth: contentMaxWidth,
            width: "100%",
            minWidth: "fit-content",
            marginLeft: contentMaxWidth !== "1920px" ? "0" : "auto",
            marginRight: "auto",
          }}
        >
          <button
            aria-label="Menu"
            className="bg-fs-surface-primary flex h-10 w-10 items-center justify-center rounded-full p-2.5 lg:hidden"
            type="button"
          >
            <div className="h-0.5 w-5 bg-[#333333]"></div>
          </button>
          {/* Variant 3 & 3b: Panel button to the left of logo */}
          {(compassPanelVariant === 3 || compassPanelVariant === 5) &&
            !isCompassPanelOpen && (
              <button
                data-test-id="compassSidebarButton"
                className="flex hidden h-10 w-10 cursor-pointer items-center justify-center rounded-[8px] border-0 bg-transparent p-0 transition-colors duration-500 ease-in-out hover:bg-[#f7f7f7] lg:flex"
                style={{ position: "absolute", left: "24px" }}
                aria-label="Compass Sidebar"
                type="button"
                onClick={onCompassClick}
              >
                <div
                  className="relative flex h-10 w-10 items-center justify-center"
                  style={{
                    transform:
                      compassPanelVariant === 3 || compassPanelVariant === 5
                        ? "scaleX(-1)"
                        : "none",
                  }}
                >
                  <SidebarIcon
                    className="h-5 w-5"
                    isOpen={isCompassPanelOpen}
                  />
                </div>
              </button>
            )}
          {disableLogoLink ? (
            <div
              id="faire-logo-link"
              aria-label="Faire Logo"
              className="relative h-6 w-[98px] shrink-0"
              style={{
                marginLeft:
                  (compassPanelVariant === 3 || compassPanelVariant === 5) &&
                  !isCompassPanelOpen
                    ? "48px"
                    : "0",
              }}
            >
              <img
                alt="Faire Logo"
                src="https://cdn.faire.com/static/logo.svg"
                className="h-full w-full object-contain"
                onError={(e) => {
                  // Fallback to MCP server if CDN fails
                  const target = e.target as HTMLImageElement;
                  target.src =
                    "http://localhost:3845/assets/1e3ffc68be20eda669774f7388f9632f2f0bab67.svg";
                }}
              />
            </div>
          ) : (
            <a
              id="faire-logo-link"
              aria-label="Go to Faire homepage"
              href={logoLinkUrl}
              onClick={handleLogoClick}
              className="relative h-6 w-[98px] shrink-0"
              style={{
                marginLeft:
                  (compassPanelVariant === 3 || compassPanelVariant === 5) &&
                  !isCompassPanelOpen
                    ? "48px"
                    : "0",
              }}
            >
              <img
                alt="Faire Logo"
                src="https://cdn.faire.com/static/logo.svg"
                className="h-full w-full object-contain"
                onError={(e) => {
                  // Fallback to MCP server if CDN fails
                  const target = e.target as HTMLImageElement;
                  target.src =
                    "http://localhost:3845/assets/1e3ffc68be20eda669774f7388f9632f2f0bab67.svg";
                }}
              />
            </a>
          )}
          {shouldShowLogoRowNavLinks
            ? renderNavLinks(
                bottomNavLinks,
                "hidden min-w-0 shrink items-center gap-1 overflow-x-auto lg:flex"
              )
            : null}
          <div
            className="hidden lg:block"
            style={{ width: "16px", height: "16px" }}
          />
          {shouldShowAllCategories ? (
            <>
              <div className="hidden lg:block">
                <button
                  aria-label="All categories"
                  aria-haspopup="menu"
                  aria-expanded={categoriesMenuOpen}
                  className={cn(
                    "text-fs-text-primary flex h-10 cursor-pointer items-center rounded-[8px] border-0 px-4 text-sm leading-5 font-normal transition-colors duration-500 ease-in-out",
                    categoriesMenuOpen
                      ? "bg-(--slate-color-action-surface-inverse-hover)"
                      : "bg-transparent hover:bg-(--slate-color-action-surface-inverse-hover)"
                  )}
                  type="button"
                  onClick={() => {
                    setCategoriesMenuOpen((open) => !open);
                    if (categoriesMenuOpen) {
                      setHoveredC1Id(null);
                      setHoveredC2Id(null);
                    }
                  }}
                >
                  All categories
                  <div style={{ width: "8px" }} />
                  {categoriesMenuOpen ? (
                    <CloseIcon className="h-3 w-3" aria-hidden />
                  ) : (
                    <ChevronDownIcon className="h-2 w-3" />
                  )}
                </button>
              </div>
              <div
                className="hidden lg:block"
                style={{ width: "16px", height: "16px" }}
              />
            </>
          ) : null}
          <div
            style={{
              flex: "1 1 0%",
              position: "relative",
              maxWidth:
                compassPanelBehavior && !isCompassPanelOpen
                  ? "calc(100% - 48px)"
                  : "100%", // Shrink by 48px (40px button + 8px gap) when panel button is visible
              transition: "max-width 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
            ref={searchContainerRef}
          >
            {shouldShowSearchBar ? (
              navSearchBar ? (
                navSearchBar
              ) : (
                <>
                  <div className="bg-fs-surface-primary flex h-10 items-center rounded-full border border-[var(--slate-color-action-border-subdued)] px-4 pr-5">
                    <input
                      id="top-search"
                      placeholder={searchPlaceholder}
                      aria-label={searchPlaceholder}
                      autoComplete="off"
                      data-test-id="searchBarInput"
                      className="text-fs-text-primary flex-1 border-0 bg-transparent text-sm outline-0 placeholder:text-[#757575]"
                      value={searchValue}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      onFocus={handleSearchFocus}
                      onKeyDown={handleSearchKeyDown}
                    />
                    <div className="flex items-center justify-center">
                      <SearchIcon className="h-4 w-4" />
                    </div>
                  </div>
                  {showSearchDropdown && isCompassEnabled && (
                    <SearchDropdown
                      ref={searchDropdownRef}
                      searchQuery={searchValue}
                      onClose={() => setShowSearchDropdown(false)}
                    />
                  )}
                </>
              )
            ) : (
              <div className="flex-1" />
            )}
          </div>
          <div
            className="hidden lg:block"
            style={{
              width:
                compassPanelBehavior && !isCompassPanelOpen ? "8px" : "16px", // Reduce gap when panel button is visible
              height: "0px",
              transition: "width 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          />
          <div
            className="hidden lg:block"
            style={{ width: "12px", height: "0px" }}
          />
          {languageSelector && (
            <>
              <div className="hidden lg:block" style={{ width: "12px" }} />
              <button
                className="hidden h-10 cursor-pointer items-center gap-2 rounded-full border-0 bg-transparent px-4 hover:bg-[#fbfbfb] lg:flex"
                aria-label="Language selector"
                type="button"
              >
                <svg
                  className="h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                </svg>
                <span className="text-fs-text-primary text-sm">EN-US</span>
              </button>
            </>
          )}
          {isCompassEnabled && (
            <>
              <div
                className={cn(
                  "transition-all duration-300 ease-in-out",
                  state.isPanelOpen
                    ? "pointer-events-none scale-95 opacity-0"
                    : "pointer-events-auto scale-100 opacity-100"
                )}
              >
                <button
                  data-test-id="compassButton"
                  onClick={togglePanel}
                  className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-[8px] border-0 bg-transparent p-0 transition-colors duration-500 ease-in-out hover:bg-[#f7f7f7]"
                  aria-label="Compass"
                  type="button"
                >
                  <div className="relative flex h-10 w-10 items-center justify-center">
                    <CompassIcon className="h-[25px] w-[25px]" />
                  </div>
                </button>
              </div>
              <div
                className={cn(
                  "hidden transition-all duration-300 ease-in-out lg:block",
                  state.isPanelOpen ? "opacity-0" : "opacity-100"
                )}
                style={{ width: "8px", height: "0px" }}
              />
            </>
          )}
          <div
            className="flex items-center gap-2"
            style={{
              marginRight:
                (compassPanelVariant === 2 || compassPanelVariant === 4) &&
                !isCompassPanelOpen
                  ? "48px"
                  : "0", // 48px gap from floating icon
              transition: "margin-right 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            {signedIn ? null : (
              <LoggedOutActions
                hideLanguageSelector={hideLanguageSelector}
                hideSellerSignup={hideSellerSignup}
                hideBlogLink={hideBlogLink}
              />
            )}
            {signedIn ? (
              <>
                {setupGuideButton && (
                  <button
                    aria-label={setupGuideButton.label || "Setup guide"}
                    type="button"
                    onClick={setupGuideButton.onClick}
                    className="text-fs-text-primary mr-2 rounded-full border border-[#dfe0e1] px-4 py-2 text-sm transition-colors duration-300 hover:border-[#333333]"
                  >
                    {setupGuideButton.label || "Setup guide"}
                  </button>
                )}
                <CommandCenterNavButton commandCenterButton={commandCenterButton} />
                {hideNotifications ? null : (
                  <button
                    data-test-id="notificationDropdown"
                    className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-[8px] border-0 bg-transparent p-0 transition-colors duration-500 ease-in-out hover:bg-[#f7f7f7]"
                    aria-label="Notifications"
                    type="button"
                    onClick={onNotificationClick || handleNotificationClick}
                  >
                    <div className="relative flex h-10 w-10 items-center justify-center">
                      <NotificationIcon className="h-5 w-5" />
                      {hasNotificationDot ? <NotificationBellDot /> : null}
                    </div>
                  </button>
                )}
                {hideAccountIcon || hideAccount ? null : (
                  <div className="relative" data-account-dropdown-root>
                    <button
                      aria-label="My Profile"
                      data-test-id="accountDropdown"
                      aria-expanded={
                        accountDropdown ? isAccountDropdownOpen : undefined
                      }
                      aria-haspopup={accountDropdown ? "menu" : undefined}
                      className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-[8px] border-0 bg-transparent p-0 transition-colors duration-500 ease-in-out hover:bg-[#f7f7f7]"
                      type="button"
                      onClick={
                        accountDropdown
                          ? toggleAccountDropdown
                          : onAccountClick || handleProfileClick
                      }
                    >
                      <div className="flex h-10 w-10 items-center justify-center">
                        <div className="relative">
                          <AccountIcon className="h-6 w-6" />
                          {accountOverdueDot && (
                            <div className="absolute -top-[3px] -right-[3px] size-[8px] rounded-full bg-[#d14a3e]" />
                          )}
                        </div>
                      </div>
                    </button>
                    {accountDropdown && isAccountDropdownOpen ? (
                      <div className="absolute top-full -right-12 z-[700] mt-2">
                        {accountDropdown(closeAccountDropdown)}
                      </div>
                    ) : null}
                  </div>
                )}
                {onFaireAiClick && (
                  <button
                    aria-label="Faire AI"
                    className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-[8px] border-0 bg-transparent p-0 transition-colors duration-500 ease-in-out hover:bg-(--slate-color-action-surface-inverse-hover)"
                    type="button"
                    onClick={onFaireAiClick}
                    style={{
                      color: isFaireAiOpen ? "#333" : undefined,
                    }}
                  >
                    <div className="relative flex h-10 w-10 items-center justify-center">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M6.99984 3.33301C6.99984 7.13048 8.92389 9.25706 13.3332 9.33301C9.24456 9.33301 7.24034 11.3836 6.99984 15.333C6.99984 11.6115 5.23612 9.33301 0.666504 9.33301C4.75511 9.25706 6.99984 7.43427 6.99984 3.33301Z"
                          stroke="#333333"
                          strokeWidth="0.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M12.6667 0.666992C12.6667 2.39248 13.451 3.33366 15.3333 3.33366C13.6078 3.33366 12.7451 4.27484 12.6667 6.00033C12.6667 4.35327 11.8824 3.33366 10 3.33366C11.7255 3.33366 12.6667 2.54935 12.6667 0.666992Z"
                          stroke="#333333"
                          strokeWidth="0.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </button>
                )}
                {hideCartIcon || hideCart ? null : (
                  <NavCartControl
                    cartHref={cartHref}
                    cartCount={cartCount}
                    onCartClick={onCartClick}
                    className="relative flex items-center justify-center rounded-[8px] transition-colors duration-500 ease-in-out hover:bg-(--slate-color-action-surface-inverse-hover)"
                    style={{ height: "unset", width: "40px" }}
                  />
                )}
                {sidecartNavButton && (
                  <button
                    data-test-id="sidecartNavButton"
                    aria-label={sidecartNavButton.label ?? "Cart sidebar"}
                    className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-[8px] border-0 bg-transparent p-0 transition-colors duration-500 ease-in-out hover:bg-[#f7f7f7]"
                    type="button"
                    onClick={sidecartNavButton.onClick}
                  >
                    <div className="relative flex h-10 w-10 items-center justify-center">
                      <SidebarIcon
                        className="h-5 w-5"
                        isOpen={sidecartNavButton.isOpen}
                      />
                    </div>
                  </button>
                )}
                {compassPanelBehavior && !isCompassPanelOpen && (
                  <button
                    data-test-id="compassSidebarButton"
                    className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-[8px] border-0 bg-transparent p-0 transition-colors duration-500 ease-in-out hover:bg-[#f7f7f7]"
                    aria-label="Compass Sidebar"
                    type="button"
                    onClick={onCompassClick}
                  >
                    <div className="relative flex h-10 w-10 items-center justify-center">
                      <SidebarIcon
                        className="h-5 w-5"
                        isOpen={isCompassPanelOpen}
                      />
                    </div>
                  </button>
                )}
              </>
            ) : null}
          </div>
        </div>

        {/* Backdrop (30% black) and category menu dropdown when open */}
        {shouldShowAllCategories && categoriesMenuOpen ? (
          <>
            <button
              type="button"
              aria-label="Close category menu"
              className="fixed right-0 bottom-0 left-0 z-650 cursor-default bg-(--slate-color-grey-1000)/30"
              style={{ top: navRowHeight || 72 }}
              onClick={() => {
                setCategoriesMenuOpen(false);
                setHoveredC1Id(null);
                setHoveredC2Id(null);
              }}
            />
            <div
              className="bg-fs-surface-primary absolute top-full right-0 left-0 w-full pt-4 pb-4 shadow-lg"
              style={{ zIndex: 700 }}
            >
              <div
                className="w-full px-4 lg:pr-12 lg:pl-[calc(3rem+98px+16px)]"
                style={{
                  maxWidth: contentMaxWidth,
                  marginLeft: contentMaxWidth !== "1920px" ? "0" : "auto",
                  marginRight: "auto",
                }}
              >
                <CategoryMenuPanel
                  categoryTree={categoryTree}
                  hoveredC1Id={hoveredC1Id}
                  hoveredC2Id={hoveredC2Id}
                  onHoverC1={setHoveredC1Id}
                  onHoverC2={setHoveredC2Id}
                  onMouseLeavePanel={() => {
                    setHoveredC1Id(null);
                    setHoveredC2Id(null);
                  }}
                  firstColumnFontSize={categoryMenuStyling?.firstColumnFontSize}
                  showColumnBorders={categoryMenuStyling?.showColumnBorders}
                  promotedColumn={categoryMenuPromotedColumn}
                />
              </div>
            </div>
          </>
        ) : null}
      </div>

      {/* Bottom Nav Links */}
      {shouldShowBottomNavLinks ? (
        <div
          className={cn(
            "bg-fs-surface-primary hidden min-h-[36px] w-full flex-row justify-center lg:flex",
            hasBottomNavPopovers ? "overflow-visible" : "overflow-x-auto"
          )}
        >
          <div
            className="bg-fs-surface-primary relative flex transition-all duration-300 ease-out"
            style={{
              maxWidth: contentMaxWidth,
              width: "100%",
              marginLeft: contentMaxWidth !== "1920px" ? "0" : "auto",
              marginRight: "auto",
            }}
          >
            <div
              data-test-id="desktop-header-c1-categories"
              className={cn(
                "relative flex w-full flex-row flex-nowrap justify-center-safe px-6 pt-1 pb-1",
                hasBottomNavPopovers ? "overflow-visible" : "overflow-x-auto"
              )}
            >
              {renderNavLinks(
                bottomNavLinks,
                "flex flex-row flex-nowrap justify-center-safe"
              )}
              <div className="invisible absolute bottom-0 w-full" />
            </div>
          </div>
        </div>
      ) : null}
    </header>
  );
}

export default function RetailerGlobalNavLoggedIn(
  props: RetailerGlobalNavLoggedInProps
) {
  return (
    <SurfacesMenuProvider>
      <SurfacesMenuOverlay />
      <RetailerGlobalNavLoggedInInner {...props} />
    </SurfacesMenuProvider>
  );
}
